// Taşeron portalı — durum → `Tag` rengi katmanı (S37-2, Carbon 2a). `Badge.tsx`'in `BadgeTone`
// yardımcılarının yerine geçer (tüketiciler sayfa sayfa buraya taşınır; `Badge.tsx` son tüketici
// gidince S37-7'de silinir). Eşleme web-expert `contractors/tone.ts` + `permits/permitEnums.ts`
// ile AYNIDIR (app sınırı: import değil, kopya — kayma riski BACKLOG'da). Yalnız 4 renk:
// mavi/kırmızı/yeşil/gri (turuncu/sarı YOK). "Dikkat" durumları gri + ikon (renk tek başına
// durum taşımaz — ikon çağıran ekrandadır).

import {
  type ComplianceStatus,
  type ContractorAccessLevel,
  type ContractorPersonnelDto,
  type ContractorPersonnelStatus,
  type PermitStatus,
  type PersonnelDocumentApprovalStatus,
  type PersonnelStatusKey,
  type TagColor,
  personnelStatusKey,
} from '@isgportal/shared';

/**
 * İzin durumu → renk + etiket anahtarı (design review M3 — web-expert
 * `getPermitStatusPresentation` ile satır satır aynı). Legacy `Approved` satırları
 * `AwaitingSupplierInfo` olarak sunulur (api-contract §30.1): etiket VE renk.
 */
export function permitStatusPresentation(status: PermitStatus): {
  color: TagColor;
  labelKey: string;
} {
  switch (status) {
    case 'Approved':
    case 'AwaitingSupplierInfo':
      return { color: 'blue', labelKey: 'permits.status.AwaitingSupplierInfo' };
    case 'AwaitingEntryPermit':
      return { color: 'blue', labelKey: 'permits.status.AwaitingEntryPermit' };
    case 'Active':
      return { color: 'green', labelKey: 'permits.status.Active' };
    case 'Rejected':
      return { color: 'red', labelKey: 'permits.status.Rejected' };
    case 'Expired':
      return { color: 'red', labelKey: 'permits.status.Expired' };
    default:
      // Draft | Submitted | AwaitingAiReview | DepartmentReview | Revision | Closed
      return { color: 'gray', labelKey: `permits.status.${status}` };
  }
}

/** Personel hazırlık etiketi (§17 türetimi): hazır → yeşil; HAZIR DEĞİL → kırmızı (M3, expert
 * `PermitPersonnelPanel`). */
export function readinessColor(ready: boolean): TagColor {
  return ready ? 'green' : 'red';
}

/** Araç hazırlık etiketi (§23): `readinessColor` ile aynı eşleme, semantik ayrık kalsın. */
export function vehicleReadyColor(ready: boolean): TagColor {
  return ready ? 'green' : 'red';
}

/**
 * Uygunluk / belge geçerliliği (§13 türetilmiş durum): `Valid` yeşil; `Expired`/`Missing`
 * kırmızı; `Expiring` gri (+ ikon çağıranda — renk tek başına taşımaz).
 */
export function complianceColor(status: ComplianceStatus): TagColor {
  switch (status) {
    case 'Valid':
      return 'green';
    case 'Expired':
    case 'Missing':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Personel/davet durumu (§13.3/§13.6): `Active` yeşil, gerisi gri.
 * `NoLogin` GRİ (design review M1 — api-contract.md:1735 lossy status; yeşil "aktif" izlenimi
 * vermez), `Pending` gri, `Inactive`/`Expired` gri.
 */
export function personnelStatusColor(status: ContractorPersonnelStatus): TagColor {
  switch (status) {
    case 'Active':
      return 'green';
    default:
      return 'gray';
  }
}

const STATUS_LABEL_KEYS: Record<PersonnelStatusKey, string> = {
  active: 'personnel.status.active',
  inactive: 'personnel.status.inactive',
  notactivated: 'personnel.status.notactivated',
  pending: 'personnel.status.pending',
  expired: 'personnel.status.expired',
};

/** Durum etiketi anahtarı: `NoLogin` + aktif → "Etkinleştirilmedi", `NoLogin` + pasif → "Pasif" (GL6 §4.1). */
export function personnelStatusLabelKey(
  row: Pick<ContractorPersonnelDto, 'status' | 'isActive'>,
): string {
  return STATUS_LABEL_KEYS[personnelStatusKey(row)];
}

/** Erişim seviyesi (§13.3): `Yetkili` mavi, `Personeli` gri. */
export function accessLevelColor(level: ContractorAccessLevel): TagColor {
  return level === 'Yetkili' ? 'blue' : 'gray';
}

/**
 * Belge onay durumu (§15.4 personel belgesi / §23 araç evrakı — aynı üç değer): `Approved` yeşil,
 * `Rejected` kırmızı (red gerekçesi ayrıca gösterilir), `Pending` gri (İSG uzmanı bekleniyor).
 * Eşleme S29 notundaki "silme yalnız Pending" kuralıyla birlikte okunur (`personnel/PersonnelDocuments.tsx`).
 */
export function docApprovalColor(status: PersonnelDocumentApprovalStatus): TagColor {
  switch (status) {
    case 'Approved':
      return 'green';
    case 'Rejected':
      return 'red';
    default:
      return 'gray';
  }
}
